import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Helmet } from "react-helmet-async";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [validSession, setValidSession] = useState(false);
  
  const [mfaCode, setMfaCode] = useState("");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if we're in password recovery mode
    const checkRecoverySession = async () => {
      try {
        // Get the session - this will include recovery sessions from email links
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Session error:", error);
          toast.error("Error loading reset page. Please try the link again.");
          navigate("/login");
          return;
        }

        if (session) {
          console.log("Valid recovery session found");
          // Check for MFA factors immediately after validating session
          const { data: mfaData } = await supabase.auth.mfa.listFactors();
          if (mfaData && mfaData.totp && mfaData.totp.length > 0) {
            setFactorId(mfaData.totp[0].id);
            toast.info("MFA is enabled on your account. Enter your 6-digit code below.");
          }
          setValidSession(true);
          setInitializing(false);
        } else {
          toast.error("Invalid or expired reset link. Please request a new one.");
          setInitializing(false);
          navigate("/login");
        }
      } catch (err) {
        console.error("Recovery check error:", err);
        toast.error("Something went wrong. Please try again.");
        setInitializing(false);
        navigate("/login");
      }
    };
    
    checkRecoverySession();
  }, [navigate]);

  const validatePassword = (pwd: string): boolean => {
    if (pwd.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return false;
    }
    if (!/[A-Z]/.test(pwd)) {
      toast.error("Password must contain at least one uppercase letter");
      return false;
    }
    if (!/[a-z]/.test(pwd)) {
      toast.error("Password must contain at least one lowercase letter");
      return false;
    }
    if (!/[0-9]/.test(pwd)) {
      toast.error("Password must contain at least one number");
      return false;
    }
    return true;
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePassword(password)) {
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      // If MFA is enabled, verify code first then update password
      if (factorId) {
        if (mfaCode.length !== 6) {
          toast.error("Enter your 6-digit MFA code");
          setLoading(false);
          return;
        }
        const challenge = await supabase.auth.mfa.challenge({ factorId });
        if (challenge.error) throw challenge.error;

        const verify = await supabase.auth.mfa.verify({
          factorId,
          challengeId: challenge.data.id,
          code: mfaCode,
        });
        if (verify.error) throw verify.error;
      }

      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      toast.success("Password updated successfully! Please log in with your new password.");
      await supabase.auth.signOut();
      navigate("/login");
    } catch (error: any) {
      console.error("Reset password error:", error);
      toast.error(error.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };


  if (initializing) {
    return null;
  }

  if (!validSession) {
    return null;
  }


  return (
    <>
      <Helmet>
        <title>Reset Password | Secure Account Access</title>
        <meta name="description" content="Reset your password and verify two-factor authentication to secure your account." />
        <link rel="canonical" href={`${window.location.origin}/reset-password`} />
      </Helmet>
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/10 via-accent/5 to-background px-4">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <Link to="/" className="inline-flex items-center gap-2">
              <Logo className="h-20 w-20" />
              <span className="text-2xl font-bold">LoopBaan.ai</span>
            </Link>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                Reset Your Password
              </CardTitle>
              <CardDescription>
                Enter your new password below. Make sure it's strong and secure.
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleResetPassword}>
              <CardContent className="space-y-4">
                {factorId && (
                  <div className="space-y-2">
                    <Label htmlFor="mfaCode">Authentication Code</Label>
                    <Input
                      id="mfaCode"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="000000"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                      required
                      autoFocus
                      className="text-center text-2xl tracking-widest"
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter the 6-digit code from your authenticator app.
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoFocus={!factorId}
                  />
                  <p className="text-xs text-muted-foreground">
                    Must be at least 8 characters with uppercase, lowercase, and numbers
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-4">
                <Button type="submit" className="w-full" disabled={loading || (factorId ? mfaCode.length !== 6 : false)}>
                  {loading
                    ? (factorId ? "Verifying & Updating..." : "Updating Password...")
                    : (factorId ? "Verify & Update Password" : "Update Password")}
                </Button>
                <Link to="/login" className="text-center text-sm text-muted-foreground hover:underline">
                  Back to Login
                </Link>
              </CardFooter>
            </form>
          </Card>

          <div className="mt-8 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Logo className="h-5 w-5" />
            <span>LoopBaan.ai</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
